import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { useAtomValue, useSetAtom } from 'jotai';

import { Button, DrawerBody, DrawerFooter, IconButton, Text } from '@growtherapy/sprout-ui';

import {
  MeasureConnected,
  MeasureProps,
  MeasureSchemaType,
  useGetMeasureSchemaByType,
} from '@grow-therapy-team/seedling-components';

import { DrawerHeader } from '../../components/DrawerHeader';
import { ProviderDrawerState, drawerStateAtom, selectedClientInfoAtom, selectedClientUserShortIdAtom } from '../state';
import { useGetPatientByShortId } from '../useGetPatientByShortId';
import { useSubmitCSSRSMeasureCallback } from './useSubmitCSSRSMeasureCallback';

type CSSRSFormProps = {
  onClickBackButton: () => void;
  onSubmit: () => Promise<void>;
  connectedMeasureComponent: React.ReactNode;
  isLoading?: boolean;
};

const CSSRSFormHeader = ({ onClickBackButton }: { onClickBackButton: () => void }) => {
  return (
    <DrawerHeader>
      <div className='flex items-center'>
        <IconButton
          onClick={onClickBackButton}
          className='mr-4 w-8 h-8 flex justify-center items-center'
          iconSize='xs'
          aria-label='Go back'
          iconDefinition={faArrowLeft}
        />
        <Text variant='lg'>C-SSRS – Suicide Risk</Text>
      </div>
    </DrawerHeader>
  );
};

export const CSSRSForm = ({ onClickBackButton, onSubmit, connectedMeasureComponent, isLoading }: CSSRSFormProps) => {
  return (
    <>
      <CSSRSFormHeader onClickBackButton={onClickBackButton} />
      <DrawerBody>{connectedMeasureComponent}</DrawerBody>
      <DrawerFooter>
        <Button loading={isLoading} onClick={onSubmit} className='!ml-auto'>
          Submit
        </Button>
      </DrawerFooter>
    </>
  );
};

export const CSSRSFormWrapper = () => {
  const clientUserShortId = useAtomValue(selectedClientUserShortIdAtom);
  const clientInfo = useAtomValue(selectedClientInfoAtom);
  const patientShortId = clientInfo?.patientShortId;
  const { data: cssrsSchemaData } = useGetMeasureSchemaByType(MeasureSchemaType.COLUMBIA_SUICIDE_SEVERITY_RATING_SCALE);
  const { loading: isLoadingPatientData } = useGetPatientByShortId(patientShortId);
  const [handleSubmitMeasure, { isLoading }] = useSubmitCSSRSMeasureCallback();

  const setDrawerState = useSetAtom(drawerStateAtom);
  const onClickBackButton = () => {
    setDrawerState(ProviderDrawerState.MEASURE_RESPONSES);
  };

  if (!cssrsSchemaData?.measureSchema || (!clientUserShortId && !isLoadingPatientData)) {
    return;
  }

  return (
    <CSSRSForm
      connectedMeasureComponent={<MeasureConnected schema={cssrsSchemaData.measureSchema as MeasureProps['schema']} />}
      onClickBackButton={onClickBackButton}
      onSubmit={handleSubmitMeasure}
      isLoading={isLoading || isLoadingPatientData}
    />
  );
};
